/** Detail Command K and header actions; menu-only placement is an explicit surface opt-in. */
import { Command } from '@workbuddy/ui/model';
import { resolveCommandSemanticId, type SemanticCommandId, type ShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import type { CommandPaletteContext } from '@workbuddy/ui/v2/blocks/command.palette';
import { buildEntityBulkActionSettingsPanel } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import type { WbActionCommand } from '@workbuddy/ui/v2/components/action.bar';

export const detailCommandsPaletteId = 'surface-detail.commands';
export const detailShortcutScope: ShortcutScope = { kind: 'page', pageId: 'surface-detail' };
/** Above Manage (30), below board selection (40), so a focused record wins until rows are selected. */
export const DETAIL_COMMANDS_PALETTE_PRIORITY = 35;

/** Detail's header preserves primary actions by default; an explicit menu-only surface folds every action. */
export function headerDetailActionCommands(commands: readonly WbActionCommand[], menuOnly = false): WbActionCommand[] {

  if (menuOnly) return commands.map(command => ({ ...command, pin: 'never' }));
  return commands.map(command => command.pin === 'never' ? command : { ...command, pin: 'always' });
}

export function canOpenDetailCommands(initializing: boolean, commands: readonly WbActionCommand[], recordId: unknown): boolean {

  return !initializing && typeof recordId === 'string' && commands.some(command => !command.disabled);
}

export function toDetailPaletteCommand(command: WbActionCommand): Command {

  return new Command({ _id: command.id, label: command.label, iconLeft: command.iconLeft, disabled: command.disabled ?? false, tooltip: command.tooltip, data: { commandPalette: { settingsPanel: { kind: 'action' } } } });
}

export function resolveDetailShortcutTarget(semanticId: SemanticCommandId, commands: readonly WbActionCommand[]): WbActionCommand | null {

  return commands.find(command => !command.disabled && resolveCommandSemanticId(command.id) === semanticId) ?? null;
}

export function buildDetailCommandsPalette(title: string, commands: readonly WbActionCommand[], execute: (command: WbActionCommand) => void, cancel: () => void): CommandPaletteContext {

  const paletteCommands = commands.map(toDetailPaletteCommand);
  return {
    id: detailCommandsPaletteId,
    scopeChip: { label: title },
    settingsPanel: buildEntityBulkActionSettingsPanel({
      title: 'Commands',
      groups: [{ label: 'Actions', commands: paletteCommands }],
      execute: paletteCommand => {
        const surface = commands.find(command => command.id === paletteCommand._id);
        if (surface) execute(surface);
      },
      cancel,
    }),
  };
}
